import { useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { If } from "@/components/If";
import { CreateLoaderTable } from "@/components/Loaders";
import { usePermissions } from "@/hooks/usePermissions";
import { ApiDataProps } from "@/pages/Tenants";
import {
  CashFlowFilters,
  getFinancialCashFlowData,
} from "@/Api/financialCashFlow";
import { CashFlowItemProps, CashFlowTotals } from "@/types/cashFlow";
import { Filters, getDefaultCashFlowFilters } from "@/App/CashFlow/Filters";
import { SummaryCards } from "@/App/CashFlow/SummaryCards";
import { TableData } from "@/App/CashFlow/Table";

const EMPTY_TOTALS: CashFlowTotals = { inflow: 0, outflow: 0, balance: 0 };

export const PageList = () => {
  const [cashFlow, setCashFlow] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
    total: 0,
  } as unknown as ApiDataProps<CashFlowItemProps>);
  const [totals, setTotals] = useState<CashFlowTotals>(EMPTY_TOTALS);
  const [pendingTotals, setPendingTotals] =
    useState<CashFlowTotals>(EMPTY_TOTALS);
  const [filters, setFilters] = useState<CashFlowFilters>(
    getDefaultCashFlowFilters()
  );
  const [state, setState] = useState("loading");
  const { hasPermission } = usePermissions();

  const fetchData = async (currentFilters: CashFlowFilters, page = 1) => {
    setState("loading");
    try {
      const { data } = await getFinancialCashFlowData({
        ...currentFilters,
        page,
      });
      setCashFlow(data?.response);
      setTotals(data?.response?.totals ?? EMPTY_TOTALS);
      setPendingTotals(data?.response?.pending_totals ?? EMPTY_TOTALS);
    } catch (error) {
      console.error("Erro ao buscar fluxo de caixa:", error);
    } finally {
      setState("production");
    }
  };

  useEffect(() => {
    fetchData(filters);
  }, []);

  const handleSearch = (newFilters: CashFlowFilters) => {
    setFilters(newFilters);
    fetchData(newFilters);
  };

  return (
    <Main>
      <If condition={hasPermission("view-cash-flow")}>
        <PageHeader
          icon={Landmark}
          title="Fluxo de Caixa"
          description="Entradas e saídas conciliadas e aprovadas; o restante aparece como a conciliar"
        />

        <Filters onSearch={handleSearch} />
        <SummaryCards totals={totals} pendingTotals={pendingTotals} />

        {state === "loading" ? (
          <CreateLoaderTable cols={4} lines={6} />
        ) : (
          <TableData
            dataApi={cashFlow}
            onPageChange={(page) => fetchData(filters, page)}
          />
        )}
      </If>
    </Main>
  );
};
